import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { SearchInput } from '../components/SearchInput.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import {
  contractorTypeOptions,
  clusterRegionOptions,
  stationOptions,
} from '../lib/companyInfo.ts';
import {
  distinctVendors,
  filterExpiringRows,
  type ExpiringFilters,
} from '../lib/expiringFilters.ts';
import { formatDateOnly } from '../lib/expiry.ts';
import type { Company, CompanyStatus } from '../lib/types.ts';

type ExpiringRow = {
  id: string;
  file_name: string;
  file_path: string;
  expires_at: string;
  created_at: string;
  option_key: string | null;
  submission: {
    id: string;
    question_id: string;
    company_id: string;
    company: {
      id: string;
      company_name: string;
      contact_name: string;
      email: string;
      status: CompanyStatus;
    } | null;
    question: { id: string; label: string } | null;
  } | null;
};

type ExpiringResponse = {
  expired: ExpiringRow[];
  expiring: ExpiringRow[];
  horizon_days: number;
};

const EMPTY_FILTERS: ExpiringFilters = {
  company: { contractorTypes: [], clusterRegions: [], stations: [] },
  query: '',
  vendorIds: [],
  status: 'all',
  expiryFrom: '',
  expiryTo: '',
};

export function AdminExpiringDocuments() {
  const [filters, setFilters] = useState<ExpiringFilters>(EMPTY_FILTERS);

  const { settings } = usePortalSettings();

  const { data, isLoading } = useQuery({
    queryKey: ['expiring-documents'],
    queryFn: () => adminApi.get<ExpiringResponse>('/api/companies/files/expiring?days=30'),
  });

  // Reuse the companies list (shared query key with Dashboard — already in cache)
  // to join contractor_types / cluster_region / station onto each document row.
  const { data: companiesData, isSuccess: companiesLoaded } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });

  // Build a lookup map from company id → filterable fields. Hooks must come
  // before any early return so the call order is stable across renders.
  const companyById = useMemo(() => {
    const map = new Map<string, Company>();
    for (const c of companiesData?.companies ?? []) map.set(c.id, c);
    return map;
  }, [companiesData]);

  const filterOptions = useMemo(
    () => ({
      contractorTypes: contractorTypeOptions(settings),
      clusterRegions: clusterRegionOptions(settings),
      stations: stationOptions(settings),
    }),
    [settings],
  );

  if (isLoading) return <CenteredSpinner />;

  const expired = data?.expired ?? [];
  const expiring = data?.expiring ?? [];

  // Vendor options come from BOTH sets so the dropdown is stable as the status
  // filter narrows the table.
  const vendors = distinctVendors([...expired, ...expiring]);
  const vendorIdValues = vendors.map((v) => v.id);
  const vendorById = new Map(vendors.map((v) => [v.id, v]));

  const filtersActive =
    filters.query.trim() !== '' ||
    filters.vendorIds.length > 0 ||
    filters.status !== 'all' ||
    filters.expiryFrom !== '' ||
    filters.expiryTo !== '' ||
    filters.company.contractorTypes.length > 0 ||
    filters.company.clusterRegions.length > 0 ||
    filters.company.stations.length > 0;

  // Don't apply the company-dimension filter until the companies query has loaded
  // (otherwise every row would drop while it is in flight). query / vendor / date
  // don't depend on it.
  const appliedFilters: ExpiringFilters = companiesLoaded
    ? filters
    : { ...filters, company: EMPTY_FILTERS.company };

  const showExpired = filters.status !== 'expiring';
  const showExpiring = filters.status !== 'expired';

  const filteredExpired = showExpired ? filterExpiringRows(expired, appliedFilters, companyById) : [];
  const filteredExpiring = showExpiring ? filterExpiringRows(expiring, appliedFilters, companyById) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Expiring documents</h1>
        <p className="mt-1 text-sm text-slate-500">
          Uploaded documents that have expired or are due to expire within{' '}
          {data?.horizon_days ?? 30} days. Vendors with expired documents are flagged non-compliant.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {/* Search + every filter share one wrapping row (mirrors the Dashboard
            and Vendors list); the search box flexes to fill the leftover width
            on sm+ and takes its own line, with controls wrapping below, on
            narrow viewports. */}
        {/* Each control grows (flex-1) to an equal share of its line so the row
            fills the width evenly with no trailing gap; min-widths force a clean
            wrap instead of one squished line. Search gets a 2× grow so it stays
            the widest control. The three dimension dropdowns are inlined (rather
            than via the shared FilterBar) so each is its own growing cell. */}
        <div className="flex flex-wrap items-stretch gap-2">
          <SearchInput
            className="w-full sm:w-auto sm:flex-[2] sm:min-w-[16rem]"
            value={filters.query}
            onChange={(query) => setFilters((f) => ({ ...f, query }))}
            placeholder="Search documents, vendors or questions…"
          />

          {/* Contractor type / cluster region / station dimensions lead the
              filter set; the document-specific filters follow. */}
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Contractor type"
            unitLabel="contractor types"
            values={filterOptions.contractorTypes}
            selected={filters.company.contractorTypes}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, contractorTypes: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Cluster region"
            unitLabel="cluster regions"
            values={filterOptions.clusterRegions}
            selected={filters.company.clusterRegions}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, clusterRegions: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Station"
            unitLabel="stations"
            values={filterOptions.stations}
            selected={filters.company.stations}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, stations: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Vendor"
            unitLabel="vendors"
            values={vendorIdValues}
            selected={filters.vendorIds}
            onChange={(vendorIds) => setFilters((f) => ({ ...f, vendorIds }))}
            labelFor={(id) => vendorById.get(id)?.name ?? id}
            subtitleFor={(id) => vendorById.get(id)?.email}
          />

          {/* Document status segmented control */}
          <div className="flex overflow-hidden rounded-md border border-slate-200 sm:flex-1 sm:min-w-[11rem]">
            {(['all', 'expired', 'expiring'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, status: s }))}
                className={`flex-1 whitespace-nowrap px-3 py-2 text-sm transition ${
                  filters.status === s
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {s === 'all' ? 'All' : s === 'expired' ? 'Expired' : 'Expiring soon'}
              </button>
            ))}
          </div>

          {/* Expiry date range */}
          <label className="flex items-center gap-1 text-sm text-slate-600 sm:flex-1 sm:min-w-[9rem]">
            <span className="text-xs text-slate-500">From</span>
            <input
              type="date"
              value={filters.expiryFrom}
              onChange={(e) => setFilters((f) => ({ ...f, expiryFrom: e.target.value }))}
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex items-center gap-1 text-sm text-slate-600 sm:flex-1 sm:min-w-[9rem]">
            <span className="text-xs text-slate-500">To</span>
            <input
              type="date"
              value={filters.expiryTo}
              onChange={(e) => setFilters((f) => ({ ...f, expiryTo: e.target.value }))}
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          </label>

          {filtersActive && (
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <Section
        title="Expired"
        rows={filteredExpired}
        emptyHint={
          filtersActive ? 'No expired documents match the current filters.' : 'No expired documents.'
        }
        tone="danger"
      />
      <Section
        title="Expiring soon"
        rows={filteredExpiring}
        emptyHint={
          filtersActive
            ? 'No upcoming expiries match the current filters.'
            : 'No upcoming expiries in the next 30 days.'
        }
        tone="warn"
      />
    </div>
  );
}

function Section({
  title,
  rows,
  emptyHint,
  tone,
}: {
  title: string;
  rows: ExpiringRow[];
  emptyHint: string;
  tone: 'danger' | 'warn';
}) {
  if (rows.length === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
        <EmptyState title={title} description={emptyHint} />
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title} <span className="text-slate-400">({rows.length})</span>
      </h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Document</th>
              <th className="px-4 py-2">Vendor</th>
              <th className="px-4 py-2">Question</th>
              <th className="px-4 py-2">Expires</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const company = row.submission?.company;
              const companyId = company?.id ?? row.submission?.company_id;
              const questionId = row.submission?.question_id;
              // Deep-link to the company's document/review view. When a question
              // id is available, append ?question=<id> so the admin lands on
              // the specific document that needs updating (mirrors the vendor's
              // ?question= deep-link pattern).
              const companyHref = companyId
                ? questionId
                  ? `/admin/companies/${companyId}?question=${questionId}`
                  : `/admin/companies/${companyId}`
                : null;
              return (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {companyHref ? (
                      <Link to={companyHref} className="hover:underline">
                        {row.file_name}
                      </Link>
                    ) : (
                      row.file_name
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {company ? (
                      <Link to={`/admin/companies/${company.id}`} className="hover:underline">
                        {company.company_name}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {company && <div className="text-xs text-slate-500">{company.email}</div>}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {row.submission?.question?.label ?? '—'}
                  </td>
                  <td
                    className={`px-4 py-2 ${tone === 'danger' ? 'text-red-700' : 'text-amber-700'}`}
                  >
                    {formatDateOnly(row.expires_at)}
                  </td>
                  <td className="px-4 py-2">
                    {company ? <CompanyStatusBadge status={company.status} /> : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
